import './index.scss';

import { useState } from 'react';

import {
  Button,
  Modal,
  Select,
  Spin,
  Form,
  Input,
  message,
  InputNumber,
} from 'antd';

import DataSearch from '../DataSearch';
import { requestWithAuth } from '../../services/api';

const FormModal = ({ open, onCancel, onSuccess, loading }) => {
  const [userSelected, setUserSelected] = useState(null);
  const [userCredits, setUserCredits] = useState({ plan: 0, bulk: 0 });

  const [form] = Form.useForm();

  const fetchUserCredits = (userId) => {
    requestWithAuth('vault-credits', 'GET', { user_id: userId }, null)
      .then((response) => {
        if (response && !response.error) {
          setUserCredits({
            plan: response.plan_credits,
            bulk: response.bulk_credits,
          });
        } else {
          message.error('Failed to load user credits');
          setUserCredits({ plan: 0, bulk: 0 });
        }
      })
      .catch(() => {
        message.error('Failed to load user credits');
        setUserCredits({ plan: 0, bulk: 0 });
      });
  };

  const onClearSearch = () => {
    form.setFieldsValue({ userId: null });
    setUserSelected({});
    setUserCredits({ plan: 0, bulk: 0 });
  };

  const onUserSelect = (data) => {
    setUserSelected({
      id: data.key,
      email: data.label,
      first_name: data.first_name,
      last_name: data.last_name,
    });
    form.setFieldsValue({ userId: data });
    fetchUserCredits(data.value);
  };

  const onLedgerTypeChange = (value) => {
    if (value === 'Plan') {
      form.setFieldsValue({ amount: userCredits.plan });
    } else {
      form.setFieldsValue({ amount: userCredits.bulk });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const currentAmount =
        values.ledgerType === 'Plan' ? userCredits.plan : userCredits.bulk;

      if (values.amount === currentAmount) {
        form.setFields([
          {
            name: 'amount',
            errors: ['New amount must be different from current credits'],
          },
        ]);
        return;
      }
      requestWithAuth('vault-credits', 'POST', null, {
        user_id: values.userId.value,
        ledger_type: values.ledgerType,
        new_balance: values.amount,
        file_name: values.memo?.trim() || 'Staff credit adjustment',
      })
        .then((response) => {
          if (response && response.message) {
            message.success('Credits updated');
            onSuccess();
            form.resetFields();
            setUserCredits({ plan: 0, bulk: 0 });
            setUserSelected(null);
          } else {
            onCancel();
          }
        })
        .catch(() => {});
    });
  };

  return (
    <Modal
      open={open}
      width={800}
      title="Update Credits"
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      afterClose={() => {
        form.resetFields();
        setUserCredits({ plan: 0, bulk: 0 });
        setUserSelected(null);
      }}
      className="credit-update-modal"
    >
      <Spin spinning={loading}>
        <Form labelCol={{ span: 7 }} wrapperCol={{ span: 13 }} form={form}>
          <Form.Item label="User" name="userId" rules={[{ required: true }]}>
            <DataSearch
              id="vault-user"
              placeholder="Search User"
              value={
                userSelected && userSelected.id
                  ? {
                      key: userSelected.id,
                      label: userSelected.email,
                      value: userSelected.id,
                    }
                  : null
              }
              onSelect={onUserSelect}
              onClear={onClearSearch}
            />
          </Form.Item>
          <Form.Item label="Current Plan Credits:">
            {userCredits.plan}
          </Form.Item>
          <Form.Item label="Current Bulk Credits:">
            {userCredits.bulk}
          </Form.Item>
          <Form.Item
            label="Ledger Type"
            name="ledgerType"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { label: 'Plan', value: 'Plan' },
                { label: 'Bulk', value: 'Bulk' },
              ]}
              onChange={onLedgerTypeChange}
            />
          </Form.Item>
          <Form.Item
            label="Credit Amount"
            name="amount"
            rules={[
              {
                required: true,
                type: 'number',
                min: 0,
                message: 'Must be greater than zero',
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item label="Memo" name="memo">
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

const CreditUpdateComponent = ({ onCreditsUpdate }) => {
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  return (
    <>
      <Button type="primary" onClick={() => setShowCreditsModal(true)}>
        Update Credits
      </Button>
      <FormModal
        open={showCreditsModal}
        onCancel={() => setShowCreditsModal(false)}
        onSuccess={() => {
          setShowCreditsModal(false);
          onCreditsUpdate();
          setModalLoading(false);
        }}
        loading={modalLoading}
      />
    </>
  );
};

export default CreditUpdateComponent;
